import { useState, useMemo, Fragment } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Plus, Search, Store, UtensilsCrossed, ShoppingBag, Pencil, Upload, Settings2, X, ChevronUp, ChevronDown, Globe } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { PageSpinner } from '@/components/ui/Spinner'
import { useMenuCategories, useMenuItems, useAddons, useSaveMenuCategory } from '@/hooks/useMenu'
import { ProductImportModal } from '@/components/products/ProductImportModal'
import { useProductStock } from '@/hooks/useProductStock'
import { useAuthStore } from '@/stores/authStore'
import type { MenuItemStatus, SourceType, MenuItem } from '@/types/app'

function statusBadgeVariant(status: MenuItemStatus) {
  switch (status) {
    case 'active':       return 'success'
    case 'seasonal':     return 'warning'
    case 'eighty_sixed': return 'danger'
    case 'retired':      return 'neutral'
  }
}

function statusLabel(status: MenuItemStatus) {
  switch (status) {
    case 'active':       return 'Active'
    case 'seasonal':     return 'Seasonal'
    case 'eighty_sixed': return "86'd"
    case 'retired':      return 'Retired'
  }
}

function StockBadge({ onHand }: { onHand: number | undefined }) {
  if (onHand === undefined) {
    return <span className="text-xs text-stone-300">—</span>
  }
  if (onHand <= 0) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-medium bg-danger-light text-danger-dark">
        Out of stock
      </span>
    )
  }
  if (onHand <= 5) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-medium bg-warning-light text-warning-dark">
        {onHand} left
      </span>
    )
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-medium bg-success-light text-success-dark">
      {onHand} units
    </span>
  )
}

const ALL    = '__all__'
const ADDONS = '__addons__'

function ProductRow({
  item, stock, stockLoading, catMap, showWholesale, navigate,
}: {
  item: MenuItem
  stock: number | undefined
  stockLoading: boolean
  catMap: Record<string, string>
  showWholesale: boolean
  navigate: (path: string) => void
}) {
  return (
    <Fragment key={item.id}>
      <tr
        className="hover:bg-stone-50 transition-colors cursor-pointer"
        onClick={() => navigate(`/products/items/${item.id}`)}
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded bg-brand-100 flex items-center justify-center shrink-0 overflow-hidden">
              {item.images?.[0]
                ? <img src={item.images[0]} alt="" className="w-full h-full object-cover" />
                : <span className="text-xs font-semibold text-brand-700">{item.name[0]?.toUpperCase()}</span>
              }
            </div>
            <div className="min-w-0">
              <p className="font-medium text-stone-900 truncate max-w-48">{item.name}</p>
              {item.regional_name && <p className="text-xs text-stone-400 truncate">{item.regional_name}</p>}
            </div>
          </div>
        </td>
        <td className="px-4 py-3">
          {item.upc_code ? <span className="font-mono text-xs text-stone-600">{item.upc_code}</span> : <span className="text-xs text-stone-300">—</span>}
        </td>
        <td className="px-4 py-3">
          <span className="text-xs text-stone-600">{catMap[item.category_id] ?? '—'}</span>
        </td>
        <td className="px-4 py-3 text-right">
          {item.price > 0
            ? <span className="font-medium text-stone-900">${item.price.toFixed(2)}</span>
            : <span className="text-xs text-warning-dark font-medium">No price</span>}
        </td>
        {showWholesale && (
          <td className="px-4 py-3 text-right">
            {item.wholesale_price != null
              ? <span className="font-medium text-stone-700">${item.wholesale_price.toFixed(2)}</span>
              : <span className="text-xs text-stone-300">—</span>}
          </td>
        )}
        <td className="px-4 py-3">
          {stockLoading ? <span className="text-xs text-stone-300">…</span> : <StockBadge onHand={stock} />}
        </td>
        <td className="px-4 py-3">
          <Badge variant={statusBadgeVariant(item.status)}>{statusLabel(item.status)}</Badge>
        </td>
        <td className="px-4 py-3">
          <span className={['inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-medium',
            (item.source_type as SourceType) === 'inhouse' ? 'bg-info-light text-info-dark' : 'bg-purple-100 text-purple-700',
          ].join(' ')}>
            {(item.source_type as SourceType) === 'inhouse' ? 'Inhouse' : 'Procured'}
          </span>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <span title="In-Store"  className={item.available_in_store    ? 'text-brand-500' : 'text-stone-200'}><Store size={14} /></span>
            <span title="Online Store" className={item.available_online   ? 'text-brand-500' : 'text-stone-200'}><Globe size={14} /></span>
            <span title="Catering"  className={item.available_catering    ? 'text-brand-500' : 'text-stone-200'}><UtensilsCrossed size={14} /></span>
            <span title="Wholesale" className={item.available_store_supply ? 'text-brand-500' : 'text-stone-200'}><ShoppingBag size={14} /></span>
          </div>
        </td>
        <td className="px-2 py-3" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-0.5">
            <button
              onClick={() => navigate(`/products/items/${item.id}`)}
              className="p-1.5 rounded text-stone-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
              title="Edit product"
            >
              <Pencil size={13} />
            </button>
          </div>
        </td>
      </tr>
    </Fragment>
  )
}

// Every restaurant organizes its menu differently — this has to be
// self-service, not something that needs a support ticket. "Delete" here is
// a soft-remove (is_active: false): existing products keep their category,
// they just stop showing it as an active option, so nothing gets silently
// orphaned or blocked by a foreign-key error.
function CategoriesManagerBar({
  categories, onAdd, onRename, onReorder, onRemove,
}: {
  categories: { id: string; name: string; display_order: number }[]
  onAdd: (name: string) => void
  onRename: (id: string, name: string) => void
  onReorder: (id: string, direction: -1 | 1) => void
  onRemove: (id: string) => void
}) {
  const [newName, setNewName] = useState('')
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameVal, setRenameVal] = useState('')

  function commitRename(id: string) {
    const trimmed = renameVal.trim()
    if (trimmed) onRename(id, trimmed)
    setRenamingId(null)
  }

  function addNew() {
    const trimmed = newName.trim()
    if (!trimmed) return
    onAdd(trimmed)
    setNewName('')
  }

  return (
    <div className="flex flex-wrap items-center gap-2 p-3 bg-stone-50 border border-stone-200 rounded-lg">
      <span className="text-xs font-medium text-stone-500 shrink-0">Categories</span>
      {categories.map((c, i) => (
        <div key={c.id} className="flex items-center gap-0.5 bg-white border border-stone-300 rounded-full pl-3 pr-1 py-1">
          {renamingId === c.id ? (
            <input
              autoFocus
              value={renameVal}
              onChange={e => setRenameVal(e.target.value)}
              onBlur={() => commitRename(c.id)}
              onKeyDown={e => { if (e.key === 'Enter') commitRename(c.id) }}
              className="text-xs w-24 focus:outline-none"
            />
          ) : (
            <button
              type="button"
              onClick={() => { setRenamingId(c.id); setRenameVal(c.name) }}
              className="text-xs font-medium text-stone-700 hover:text-brand-700"
              title="Click to rename"
            >
              {c.name}
            </button>
          )}
          <button
            type="button" disabled={i === 0} onClick={() => onReorder(c.id, -1)}
            className="p-0.5 text-stone-400 hover:text-stone-700 disabled:opacity-25 disabled:cursor-default"
            title="Move earlier"
          >
            <ChevronUp size={11} />
          </button>
          <button
            type="button" disabled={i === categories.length - 1} onClick={() => onReorder(c.id, 1)}
            className="p-0.5 text-stone-400 hover:text-stone-700 disabled:opacity-25 disabled:cursor-default"
            title="Move later"
          >
            <ChevronDown size={11} />
          </button>
          <button
            type="button" onClick={() => onRemove(c.id)}
            className="p-0.5 text-stone-300 hover:text-danger"
            title="Remove category (products keep it, just hidden from this list)"
          >
            <X size={11} />
          </button>
        </div>
      ))}
      <div className="flex items-center gap-1">
        <input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') addNew() }}
          placeholder="New category…"
          className="text-xs border border-dashed border-stone-300 rounded-full px-3 py-1.5 w-32 focus:outline-none focus:ring-1 focus:ring-brand-400"
        />
        <button type="button" onClick={addNew} className="p-1 text-brand-600 hover:text-brand-700" title="Add category">
          <Plus size={14} />
        </button>
      </div>
    </div>
  )
}

export function MenuPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { data: categories, isLoading: catsLoading } = useMenuCategories()
  const { data: allItems,   isLoading: itemsLoading } = useMenuItems()
  const { data: stockMap,   isLoading: stockLoading }  = useProductStock()
  const { data: addons = [] } = useAddons()
  const { mutateAsync: saveCategory } = useSaveMenuCategory()

  const [search, setSearch]               = useState('')
  const [catFilter, setCatFilter]         = useState(ALL)
  const [statusFilter, setStatusFilter]   = useState(ALL)
  const [groupByCategory, setGroupByCategory] = useState(true)
  const [importOpen, setImportOpen]       = useState(false)
  const [manageCatsOpen, setManageCatsOpen] = useState(false)

  const showWholesale = user?.role === 'owner' || user?.role === 'ops'
  // Matches the RLS policy on menu_categories writes — everyone can see the
  // list, only owner/gm can add/rename/reorder/remove.
  const canManageCategories = user?.role === 'owner' || user?.role === 'gm'

  const items = allItems ?? []
  const cats  = categories ?? []
  const catMap = Object.fromEntries(cats.map(c => [c.id, c.name]))

  async function moveCategory(id: string, direction: -1 | 1) {
    const idx = cats.findIndex(c => c.id === id)
    const newIdx = idx + direction
    if (idx === -1 || newIdx < 0 || newIdx >= cats.length) return
    const reordered = [...cats]
    const [moved] = reordered.splice(idx, 1)
    reordered.splice(newIdx, 0, moved)
    await Promise.all(reordered.map((c, i) => c.display_order === i ? null : saveCategory({ id: c.id, display_order: i })))
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return items.filter(item => {
      if (catFilter !== ALL && item.category_id !== catFilter) return false
      if (statusFilter !== ALL && item.status !== statusFilter) return false
      if (q && !item.name.toLowerCase().includes(q) &&
          !(item.regional_name ?? '').toLowerCase().includes(q) &&
          !(item.upc_code ?? '').toLowerCase().includes(q)) return false
      return true
    })
  }, [items, catFilter, statusFilter, search])

  const totalItems   = items.length
  const activeItems  = items.filter(i => i.status === 'active').length
  const storeSupply  = items.filter(i => i.available_store_supply).length
  const outOfStock   = stockMap
    ? items.filter(i => {
        const s = stockMap.get(i.id)
        return s !== undefined && s.on_hand <= 0
      }).length
    : 0

  if (catsLoading || itemsLoading) return <PageSpinner />

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="page-header">
        <h1 className="text-xl font-semibold text-stone-900">Products</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
            <Upload size={14} />
            Import
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/inventory">Ingredients</Link>
          </Button>
          {catFilter === ADDONS ? (
            <Button size="sm" asChild>
              <Link to="/products/items/new?addon=true">
                <Plus size={14} />
                Add Add-on
              </Link>
            </Button>
          ) : (
            <Button size="sm" asChild>
              <Link to="/products/items/new">
                <Plus size={14} />
                Add Product
              </Link>
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Products', value: totalItems },
          { label: 'Active',         value: activeItems },
          { label: 'Store Supply',   value: storeSupply },
          { label: 'Out of Stock',   value: outOfStock,  warn: outOfStock > 0 },
        ].map(s => (
          <div key={s.label} className="card p-4">
            <p className="section-label">{s.label}</p>
            <p className={['text-2xl font-semibold mt-1', s.warn ? 'text-danger' : 'text-stone-900'].join(' ')}>
              {s.value}
            </p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-48 max-w-72">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            type="text"
            placeholder="Search name, Telugu, UPC…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-stone-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-400 bg-white"
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={() => setCatFilter(ALL)}
            className={['px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
              catFilter === ALL ? 'bg-brand-600 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200',
            ].join(' ')}
          >
            All
          </button>
          {cats.map(c => (
            <button
              key={c.id}
              onClick={() => setCatFilter(catFilter === c.id ? ALL : c.id)}
              className={['px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
                catFilter === c.id ? 'bg-brand-600 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200',
              ].join(' ')}
            >
              {c.name}
            </button>
          ))}
          <button
            onClick={() => setCatFilter(catFilter === ADDONS ? ALL : ADDONS)}
            className={['px-2.5 py-1 rounded-full text-xs font-medium transition-colors border',
              catFilter === ADDONS
                ? 'bg-amber-500 text-white border-amber-500'
                : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
            ].join(' ')}
          >
            Add-ons {addons.length > 0 && `(${addons.length})`}
          </button>
          {canManageCategories && (
            <button
              onClick={() => setManageCatsOpen(v => !v)}
              className={[
                'p-1.5 rounded-full transition-colors',
                manageCatsOpen ? 'bg-stone-200 text-stone-700' : 'text-stone-400 hover:bg-stone-100 hover:text-stone-600',
              ].join(' ')}
              title="Manage categories"
            >
              <Settings2 size={14} />
            </button>
          )}
        </div>
        {catFilter !== ADDONS && (
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={() => setGroupByCategory(g => !g)}
              className={[
                'px-2.5 py-1.5 rounded text-xs font-medium border transition-colors',
                groupByCategory
                  ? 'bg-brand-600 text-white border-brand-600'
                  : 'bg-white text-stone-600 border-stone-300 hover:border-brand-400',
              ].join(' ')}
            >
              Group by category
            </button>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="text-xs border border-stone-300 rounded px-2 py-1.5 bg-white text-stone-600 focus:outline-none focus:ring-1 focus:ring-brand-400"
            >
              <option value={ALL}>All statuses</option>
              <option value="active">Active</option>
              <option value="seasonal">Seasonal</option>
              <option value="eighty_sixed">86'd</option>
              <option value="retired">Retired</option>
            </select>
          </div>
        )}
      </div>

      {manageCatsOpen && canManageCategories && (
        <CategoriesManagerBar
          categories={cats}
          onAdd={name => saveCategory({ name, display_order: cats.length })}
          onRename={(id, name) => saveCategory({ id, name })}
          onReorder={moveCategory}
          onRemove={id => saveCategory({ id, is_active: false })}
        />
      )}

      {/* Add-ons table */}
      {catFilter === ADDONS && (
        addons.length === 0 ? (
          <div className="card p-12 text-center">
            <p className="text-stone-400 mb-1">No add-ons yet.</p>
            <p className="text-xs text-stone-400 mb-4">Add accompaniments like chutneys, raitha, and onion mix here.</p>
            <Button asChild>
              <Link to="/products/items/new?addon=true"><Plus size={14} />Add Add-on</Link>
            </Button>
          </div>
        ) : (
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200 bg-amber-50 text-left">
                  <th className="px-4 py-2.5 text-xs font-semibold text-amber-700 uppercase tracking-wide">Add-on</th>
                  <th className="px-4 py-2.5 text-xs font-semibold text-amber-700 uppercase tracking-wide">Description</th>
                  <th className="px-4 py-2.5 text-xs font-semibold text-amber-700 uppercase tracking-wide">Recipe</th>
                  <th className="px-2 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {addons.map(addon => (
                  <tr
                    key={addon.id}
                    className="hover:bg-stone-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/products/items/${addon.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="h-7 w-7 rounded bg-amber-100 flex items-center justify-center shrink-0">
                          <span className="text-xs font-semibold text-amber-700">{addon.name[0]?.toUpperCase()}</span>
                        </div>
                        <span className="font-medium text-stone-900">{addon.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-stone-500 text-xs max-w-xs truncate">
                      {addon.description ?? <span className="text-stone-300 italic">No description</span>}
                    </td>
                    <td className="px-4 py-3">
                      {addon.ai_recipe
                        ? <span className="text-2xs font-medium text-green-700 bg-green-50 px-1.5 py-0.5 rounded">Recipe saved</span>
                        : <span className="text-2xs font-medium text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded">No recipe</span>
                      }
                    </td>
                    <td className="px-2 py-3" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => navigate(`/products/items/${addon.id}`)}
                        className="p-1.5 rounded text-stone-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
                      >
                        <Pencil size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-2.5 border-t border-stone-100 bg-stone-50 text-xs text-stone-400">
              {addons.length} add-on{addons.length !== 1 ? 's' : ''}
            </div>
          </div>
        )
      )}

      {/* Products table */}
      {catFilter !== ADDONS && (filtered.length === 0 ? (
        <div className="card p-12 text-center">
          <p className="text-stone-400 mb-4">
            {items.length === 0 ? 'No products yet.' : 'No products match your filters.'}
          </p>
          {items.length === 0 && (
            <Button asChild>
              <Link to="/products/items/new"><Plus size={14} />Add Product</Link>
            </Button>
          )}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50 text-left">
                  <th className="px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wide">Product</th>
                  <th className="px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wide">UPC</th>
                  <th className="px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wide">Category</th>
                  <th className="px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wide text-right">Counter</th>
                  {showWholesale && (
                    <th className="px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wide text-right">Wholesale</th>
                  )}
                  <th className="px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wide">Stock</th>
                  <th className="px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wide">Source</th>
                  <th className="px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wide">Available</th>
                  <th className="px-2 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {groupByCategory
                  ? [...cats]
                      .sort((a, b) => a.display_order - b.display_order)
                      .flatMap(cat => {
                        const catItems = filtered.filter(i => i.category_id === cat.id)
                        if (catItems.length === 0) return []
                        return [
                          <tr key={`cat-${cat.id}`} className="bg-stone-100 border-t-2 border-stone-200">
                            <td colSpan={showWholesale ? 10 : 9} className="px-4 py-1.5">
                              <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                                {cat.name}{' '}
                                <span className="font-normal text-stone-400 normal-case tracking-normal">({catItems.length})</span>
                              </span>
                            </td>
                          </tr>,
                          ...catItems.map(item => <ProductRow key={item.id} item={item} stock={stockMap?.get(item.id)?.on_hand} stockLoading={stockLoading} catMap={catMap} showWholesale={showWholesale} navigate={navigate} />),
                        ]
                      })
                  : filtered.map(item => (
                      <ProductRow key={item.id} item={item} stock={stockMap?.get(item.id)?.on_hand} stockLoading={stockLoading} catMap={catMap} showWholesale={showWholesale} navigate={navigate} />
                    ))
                }
              </tbody>
            </table>
          </div>

          <div className="px-4 py-2.5 border-t border-stone-100 bg-stone-50 text-xs text-stone-400">
            {filtered.length} of {items.length} products
          </div>
        </div>
      ))}

      {importOpen && <ProductImportModal onClose={() => setImportOpen(false)} />}
    </div>
  )
}
